import { ParentalControlConfig, UserProfile } from '../types/iptv';

const RATING_WEIGHTS: Record<string, number> = {
  'L': 0,
  '10': 10,
  '12': 12,
  '14': 14,
  '16': 16,
  '18': 18,
};

export function isRatingAllowed(
  contentRating: string | undefined,
  maxAllowedRating: 'L' | '10' | '12' | '14' | '16' | '18'
): boolean {
  if (!contentRating) return true;
  const clean = contentRating.trim().toUpperCase();
  if (clean === 'L' || clean === 'LIVRE' || clean === '0') return true;
  if (maxAllowedRating === 'L') return false; // Se o limite é Livre, qualquer coisa acima (10+, 12+, etc) não é permitida

  const contentWeight = RATING_WEIGHTS[clean] ?? 0;
  const maxWeight = RATING_WEIGHTS[maxAllowedRating] ?? 18;
  return contentWeight <= maxWeight;
}

export function isAdultContent(titleOrCategory: string = ''): boolean {
  const lower = titleOrCategory.toLowerCase();
  const adultTerms = ['adult', '18+', 'xxx', 'sexy', 'playboy', 'sexyhot', 'venus', 'privê', 'prive', 'erótico', 'erotico', 'erotic'];
  return adultTerms.some((term) => lower.includes(term));
}

export function shouldHideCategoryFromList(
  categoryName: string,
  profile: UserProfile,
  parentalConfig?: ParentalControlConfig
): boolean {
  if (!categoryName) return false;

  // 1. Categorias bloqueadas manualmente pelo Pai
  const profileBlocked = profile.parentalSettings?.blockedCategories || [];
  const globalBlocked = parentalConfig?.blockedCategories || [];
  if (profileBlocked.includes(categoryName) || globalBlocked.includes(categoryName)) {
    return true;
  }

  // 2. Categorias 18+ (Adulto): Só aparecem para quem tiver habilitado 18+!
  if (isAdultContent(categoryName)) {
    const profileMaxRating = profile.parentalSettings?.maxRating;
    const isAdultAllowed = !profile.isKids && (profileMaxRating === '18' || (!profileMaxRating && !profile.isKids));
    if (!isAdultAllowed || parentalConfig?.adultContentHidden) {
      return true;
    }
  }

  return false;
}

export function shouldHideContentFromList(
  item: { name?: string; title?: string; isAdult?: boolean; rating?: string; groupTitle?: string; category?: string },
  profile: UserProfile,
  parentalConfig?: ParentalControlConfig
): boolean {
  const itemTitle = item.name || item.title || '';
  const itemCategory = item.groupTitle || item.category || '';

  // 1. Se a categoria estiver oculta
  if (shouldHideCategoryFromList(itemCategory, profile, parentalConfig)) {
    return true;
  }

  // 2. Conteúdo 18+ (Adulto): Só aparece para quem tiver habilitado 18+!
  const isAdult = item.isAdult || isAdultContent(itemCategory) || isAdultContent(itemTitle);
  if (isAdult) {
    const profileMaxRating = profile.parentalSettings?.maxRating;
    const isAdultAllowed = !profile.isKids && (profileMaxRating === '18' || (!profileMaxRating && !profile.isKids));
    if (!isAdultAllowed || parentalConfig?.adultContentHidden) {
      return true;
    }
  }

  // 3. No perfil Kids: Oculta tudo que for acima de "L" (Livre)
  if (profile.isKids) {
    if (item.rating) {
      const clean = item.rating.trim().toUpperCase();
      if (clean !== 'L' && clean !== 'LIVRE' && clean !== '0') {
        return true;
      }
    }
  }

  // Para os demais perfis (10, 12, 14, 16): O conteúdo fica visível e solicita PIN ao reproduzir!
  return false;
}

export function isContentRestricted(
  item: { name?: string; title?: string; isAdult?: boolean; rating?: string; groupTitle?: string; category?: string },
  profile: UserProfile,
  parentalConfig: ParentalControlConfig,
  isParentalUnlockedThisSession: boolean
): { restricted: boolean; reason?: 'adult' | 'rating' | 'category' | 'curfew' | 'kids_mode' } {
  // If master parental lock is unlocked for the current session (e.g. adult entered PIN)
  if (isParentalUnlockedThisSession && !profile.isKids) {
    return { restricted: false };
  }

  const itemTitle = item.name || item.title || '';
  const itemCategory = item.groupTitle || item.category || '';

  // 1. Adult content check
  const isAdult = item.isAdult || isAdultContent(itemCategory) || isAdultContent(itemTitle);
  if (isAdult) {
    const profileMaxRating = profile.parentalSettings?.maxRating;
    const isAdultAllowed = !profile.isKids && (profileMaxRating === '18' || (!profileMaxRating && !profile.isKids));
    if (!isAdultAllowed || parentalConfig.adultContentHidden) {
      return { restricted: true, reason: 'adult' };
    }
  }

  // 2. Blocked category check (Configurado manualmente pelo Pai)
  const profileBlocked = profile.parentalSettings?.blockedCategories || [];
  const globalBlocked = parentalConfig.blockedCategories || [];
  if (profileBlocked.includes(itemCategory) || globalBlocked.includes(itemCategory)) {
    return { restricted: true, reason: 'category' };
  }

  // 3. Age rating check (Para 10, 12, 14, 16: solicita senha PIN se exceder o limite do perfil!)
  const profileMaxRating = profile.isKids
    ? 'L'
    : (profile.parentalSettings?.maxRating || parentalConfig.allowedMaxAgeRating || '18');

  if (item.rating && !isRatingAllowed(item.rating, profileMaxRating)) {
    return { restricted: true, reason: 'rating' };
  }

  // 4. Curfew check (e.g. 22:00 to 07:00)
  if (profile.isKids && parentalConfig.curfewStartTime && parentalConfig.curfewEndTime) {
    // Horário de BRASÍLIA (não o fuso do aparelho — sticks costumam vir em UTC)
    const now = new Date();
    let currentHours = now.getHours();
    let currentMins = now.getMinutes();
    try {
      const brt = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/Sao_Paulo' });
      const [h, m] = brt.split(':').map(Number);
      if (!isNaN(h) && !isNaN(m)) {
        currentHours = h % 24;
        currentMins = m;
      }
    } catch {}
    const currentTotalMins = currentHours * 60 + currentMins;

    const [startH, startM] = parentalConfig.curfewStartTime.split(':').map(Number);
    const [endH, endM] = parentalConfig.curfewEndTime.split(':').map(Number);
    const startTotal = startH * 60 + startM;
    const endTotal = endH * 60 + endM;

    // e.g. 22:00 (1320) to 07:00 (420)
    let inCurfew = false;
    if (startTotal > endTotal) {
      inCurfew = currentTotalMins >= startTotal || currentTotalMins < endTotal;
    } else {
      inCurfew = currentTotalMins >= startTotal && currentTotalMins < endTotal;
    }

    if (inCurfew) {
      return { restricted: true, reason: 'curfew' };
    }
  }

  return { restricted: false };
}

// Obfuscação reversível para credenciais armazenadas localmente (XOR + base64)
// Não é criptografia real, mas impede leitura trivial dos valores no localStorage
const CRED_KEY = 'OpTv2026!x';

export function encryptCredential(plainText: string): string {
  try {
    let result = '';
    for (let i = 0; i < plainText.length; i++) {
      result += String.fromCharCode(plainText.charCodeAt(i) ^ CRED_KEY.charCodeAt(i % CRED_KEY.length));
    }
    return btoa(encodeURIComponent(result));
  } catch {
    return plainText;
  }
}

export function decryptCredential(encoded: string): string {
  try {
    const xored = decodeURIComponent(atob(encoded));
    let result = '';
    for (let i = 0; i < xored.length; i++) {
      result += String.fromCharCode(xored.charCodeAt(i) ^ CRED_KEY.charCodeAt(i % CRED_KEY.length));
    }
    return result;
  } catch {
    // Fallback: tenta decodificar como base64 puro (migração de dados antigos)
    try {
      return decodeURIComponent(atob(encoded));
    } catch {
      return encoded;
    }
  }
}

/**
 * Gera hash SHA-256 para PINs de perfil com salt do sistema
 */
export async function hashPin(pin: string): Promise<string> {
  const clean = pin.trim();
  if (!clean) return '';
  try {
    if (typeof window !== 'undefined' && window.crypto?.subtle) {
      const msgBuffer = new TextEncoder().encode(`opentv_salt_${clean}`);
      const hashBuffer = await window.crypto.subtle.digest('SHA-256', msgBuffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
    }
  } catch {}
  return `hash_${clean}`;
}

/**
 * Valida o PIN digitado comparando com o hash SHA-256 ou com PIN legado em texto claro
 */
export async function isPinValid(enteredPin: string, storedPinOrHash: string | undefined): Promise<boolean> {
  if (!storedPinOrHash) return true;
  const cleanEntered = enteredPin.trim();
  const cleanStored = storedPinOrHash.trim();

  // 1. Comparação direta para perfis legados
  if (cleanEntered === cleanStored) return true;

  // 2. Comparação contra hash SHA-256
  const computed = await hashPin(cleanEntered);
  return computed === cleanStored;
}

